import {
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiProduct, ApiV1Service } from 'api';
import { combineLatest, finalize, firstValueFrom, map, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RevisionsDataSource } from 'component/details/revisions-data-source';
import { ProductWithAncestors } from 'component/details/product-resolver';
import { RevisionsEditingSupport } from 'component/details/revisions-editing-support';
import {
  ProductEditModel,
  ProductHeaderComponent,
} from 'component/details/product-header/product-header.component';
import { RevisionsTableComponent } from 'component/details/revisions-table/revisions-table.component';
import { CardHeaderComponent } from 'common/card/header/card-header.component';
import { MatDialog } from '@angular/material/dialog';
import { AddRevisionComponent } from 'component/details/add/add-revision.component';
import { ButtonDirective } from 'common/button/button.directive';
import { IconDirective } from 'common/icon/icon.directive';

@Component({
  templateUrl: 'product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  imports: [
    TranslateModule,
    ProductHeaderComponent,
    RevisionsTableComponent,
    CardHeaderComponent,
    ButtonDirective,
    IconDirective,
  ],
})
export class ProductDetailsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiService = inject(ApiV1Service);

  protected readonly details = signal<ApiProduct | null>(null);

  protected readonly editModel = signal<ProductEditModel>({
    name: '',
    description: '',
  });

  protected readonly isEditing = signal(false);
  protected readonly submitting = signal(false);

  protected readonly dataSource: RevisionsDataSource = new RevisionsDataSource();
  private readonly injector = inject(Injector);

  protected readonly editingSupport = computed(() => {
    const productId = this.details()?.id;

    if (productId === undefined) {
      return undefined;
    }

    return runInInjectionContext(this.injector, () => new RevisionsEditingSupport(productId));
  });

  readonly dialog = inject(MatDialog);

  constructor() {
    this.route.data
      .pipe(
        map((data) => data['Product'] as ProductWithAncestors),
        tap((details) => this.details.set(details)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        tap(([urlParamMap, urlQueryParamMap]) => {
          const productId = Number(urlParamMap.get('productId'));

          if (Number.isInteger(productId)) {
            this.dataSource.productId = productId;
            this.dataSource.refreshFromQueryParams(urlQueryParamMap);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected openEvidences(id: number): void {
    this.router.navigate([id], { relativeTo: this.route.parent });
  }

  protected onEdit(): void {
    const details = this.details();

    if (!details) {
      return;
    }

    this.editModel.set({
      name: details.name ?? '',
      description: details.description ?? '',
    });

    this.isEditing.set(true);
  }

  protected onEditModelChanged(model: ProductEditModel): void {
    this.editModel.set(model);
  }

  protected async onSave(): Promise<void> {
    const details = this.details();

    if (!details || !this.editModel().name.trim()) {
      return;
    }

    const id = details.id;

    this.submitting.set(true);

    try {
      await firstValueFrom(
        this.apiService
          .updateProduct(id, {
            id,
            name: this.editModel().name,
            description: this.editModel().description,
            manufacturerId: details.manufacturerId,
            createdAt: details.createdAt,
          })
          .pipe(
            finalize(() => {
              this.submitting.set(false);
              this.isEditing.set(false);
            }),
          ),
      );

      this.details.set(await firstValueFrom(this.apiService.getProduct(id)));
    } catch (error) {
      console.log(error);
      this.submitting.set(false);
    }
  }

  /*  protected async addRevision() {
    await this.router.navigate(['add'], { relativeTo: this.route.parent });
  }*/

  protected async addRevision() {
    const productId = this.details()?.id;
    if (!productId) {
      console.error('Product ID is not available');
      return;
    }
    const latestRevisionPage = await firstValueFrom(
      this.apiService.getProductRevisionsForProduct(productId, 0, 1, 'createdAt', 'desc'),
    );
    const latestRevisionIntakeType =
      latestRevisionPage.items.length > 0 ? latestRevisionPage.items[0].intakeType : null;
    const dialogRef = this.dialog.open(AddRevisionComponent, {
      data: {
        latestRevisionIntakeType: latestRevisionIntakeType,
        productId: this.details()?.id,
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === undefined || !result.revision?.id) {
        return;
      }

      if (result.openRevisionAfterCreation) {
        await this.router.navigate([result.revision.id], { relativeTo: this.route });
        return;
      }

      this.dataSource.refreshFromQueryParams(this.route.snapshot.queryParamMap);
    });
  }

  protected onCancel(): void {
    this.isEditing.set(false);
  }
}
